import { defineComponent as _defineComponent } from 'vue'
import { SnowplowContext } from '@/helpers/tracking/providers';

import { CreditRole } from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';

import { streamingChartCountries } from '@/components/details/streaming-chart/streaming-chart-config';

import { formatRuntime } from '@/helpers/title-helper';
import { useLanguageStore, useRoutingStore, useUserStore } from '@/helpers/composables/useStores';
import { computed } from 'vue';
import { useI18n } from '@/helpers/composables/useI18n';

import TitleRankingList from '@/components/TitleRankingList.vue';
import TitleScoreList from '@/components/TitleScoreList.vue';
import TitleCreditName from '@/components/details/TitleCreditName.vue';
import TitleDetailStreamingChart from '@/components/details/streaming-chart/TitleDetailStreamingChart.vue';
import { activeSubgenreCountries } from '@/constants/subgenres';

type RedundantSubgenre = keyof typeof SubgenreToGenreDictionary;
type SubgenreToGenreDictionary = (typeof SubgenreToGenreDictionary)[RedundantSubgenre];
type Subgenre = TitleDetail['content']['subgenres'][number]['content'] & {
	url: { fullPath: string };
};


export default /*#__PURE__*/_defineComponent({
  __name: 'TitleInfo',
  props: {
    title: null,
    additionalContexts: { default: () => [] }
  },
  setup(__props: any) {

const props = __props as { title: TitleDetail, additionalContexts: SnowplowContext[] };

const SubgenreToGenreDictionary = {
	'Mystery Thriller': 'Mystery & Thriller',
	'Action Adventure': 'Action & Adventure',
};



const { country } = useLanguageStore();
const { isLoggedIn, isPremium } = useUserStore();

const jwVotes = computed(() =>
	props.title.content.interactions?.votesNumber ? props.title.content.interactions.votesNumber : null
);

const directors = computed(() => {
	const credits =
		props.title.__typename === 'Season' ? props.title.show.content.credits : props.title.content.credits;

	return credits.filter(credit => credit.role === CreditRole.Director);
});

const { t } = useI18n();
const runtime = computed(() => {
	const runtimeValue = props.title.content.runtime!;
	const { translationKey, minutes, hours } = formatRuntime(runtimeValue);

	return t(translationKey, { minutes, hours }) as string;
});

const { urlMetadata: contentData } = useRoutingStore();
const htmlContent = computed(() => contentData.value?.html_content ?? {});

const productionCountries = computed(() => {
	const titleContent = props.title.__typename === 'Season' ? props.title.show.content : props.title.content;
	return (titleContent.productionCountries ?? []).map(country => t(`WEBAPP_COUNTRY_${country}`)).join(', ');
});

const subgenres = computed(
	() => (props.title.content?.subgenres ?? []).map(({ content }) => content).filter(el => el.url) as Subgenre[]
);

const genres = computed(() => {
	const genresToRemove = new Set();
	subgenres.value.forEach(({ name }) => {
		if (SubgenreToGenreDictionary[name as RedundantSubgenre] != null)
			genresToRemove.add(SubgenreToGenreDictionary[name as RedundantSubgenre]);
	});

	return props.title.content.genres
		.map(genre => genre.translation)
		.filter(translation => !!translation && !genresToRemove.has(translation));
});

const showGenres = computed(() => genres.value.length > 0);

const showSubgenres = computed(() => activeSubgenreCountries.includes(country.value) && subgenres.value.length > 0);

const scoring = computed(() => props.title.content.scoring);

const isReleased = computed(() => props.title.content.isReleased);

const isStreamingChartCountry = computed(() => streamingChartCountries.includes(country.value));

return { __sfc: true,SubgenreToGenreDictionary, props, country, isLoggedIn, isPremium, jwVotes, directors, t, runtime, contentData, htmlContent, productionCountries, subgenres, genres, showGenres, showSubgenres, scoring, isReleased, isStreamingChartCountry, TitleRankingList, TitleScoreList, TitleCreditName, TitleDetailStreamingChart }
}

})